import api from './api';
import {
  DashboardStats,
  LeadsChartData,
  VisitsChartData,
  CommissionChartData,
  LeadSourceData,
  ActivityItem,
  DashboardResponse,
  TimePeriod,
} from '@/types/dashboard.types';

// Map frontend period values to backend period values
const mapPeriod = (period: TimePeriod): string => {
  const periodMap: Record<TimePeriod, string> = {
    week: 'weekly',
    month: 'monthly',
    quarter: 'monthly', // Backend doesn't have quarterly, use monthly
    year: 'yearly',
  };
  return periodMap[period] || 'monthly';
};

// Backend response interface
interface BackendDashboardStats {
  totalLeads: number;
  totalVisits: number;
  walletBalance: number;
  pendingReferrals: number;
  commissionEarned: number;
  qleadConversionRate: number;
  leadsThisMonth: number;
  visitsThisMonth: number;
  completedVisits: number;
  qleadCount: number;
}

interface BackendLeadsTrend {
  date: string;
  count: number;
}

interface BackendVisitsTrend {
  date: string;
  scheduled: number;
  completed: number;
}

// Transform backend stats to frontend format
const transformStats = (data: BackendDashboardStats): DashboardStats => ({
  leads: {
    total_this_month: data.leadsThisMonth || 0,
    total_last_month: 0, // Backend doesn't provide this
    qleads_this_month: data.qleadCount || 0,
    conversion_rate: data.qleadConversionRate || 0,
  },
  visits: {
    total_this_month: data.visitsThisMonth || 0,
    completed_this_month: data.completedVisits || 0,
    scheduled_upcoming: 0, // Backend doesn't provide this
    no_shows_this_month: 0, // Backend doesn't provide this
  },
  wallet: {
    balance: data.walletBalance || 0,
    commission_this_month: data.commissionEarned || 0,
    pending_topups: 0, // Backend doesn't provide this
  },
  referrals: {
    pending_received: data.pendingReferrals || 0,
    pending_sent: 0, // Backend doesn't provide this
    completed_this_month: 0, // Backend doesn't provide this
  },
});

// Transform backend leads trend to frontend format
const transformLeadsTrend = (data: BackendLeadsTrend[]): LeadsChartData[] =>
  data.map((item, index) => ({
    period: item.date,
    label: `Period ${index + 1}`,
    leads: item.count,
    qleads: 0, // Backend doesn't separate qleads in trend
  }));

// Transform backend visits trend to frontend format
const transformVisitsTrend = (data: BackendVisitsTrend[]): VisitsChartData[] =>
  data.map((item, index) => ({
    period: item.date,
    label: `Period ${index + 1}`,
    scheduled: item.scheduled,
    completed: item.completed,
    no_shows: item.scheduled - item.completed,
  }));

export const dashboardService = {
  /**
   * Get complete dashboard data including stats, charts, and activity
   */
  getDashboard: async (period: TimePeriod = 'month'): Promise<{ data: DashboardResponse }> => {
    const backendPeriod = mapPeriod(period);

    // Fetch all data in parallel
    // Note: API interceptor unwraps { success, data } to just data
    const [statsRes, leadsRes, visitsRes, sourcesRes, activityRes] = await Promise.all([
      api.get<BackendDashboardStats>('/stats/broker/dashboard'),
      api.get<BackendLeadsTrend[]>('/stats/broker/leads-trend', { params: { period: backendPeriod } }),
      api.get<BackendVisitsTrend[]>('/stats/broker/visits-trend', { params: { period: backendPeriod } }),
      api.get<LeadSourceData[]>('/stats/broker/lead-sources').catch(() => ({ data: [] })),
      api.get<ActivityItem[]>('/stats/broker/activity', { params: { limit: 10 } }).catch(() => ({ data: [] })),
    ]);

    return {
      data: {
        stats: transformStats(statsRes.data ?? {} as BackendDashboardStats),
        leads_chart: transformLeadsTrend(Array.isArray(leadsRes.data) ? leadsRes.data : []),
        visits_chart: transformVisitsTrend(Array.isArray(visitsRes.data) ? visitsRes.data : []),
        commission_chart: [], // Backend doesn't have commission chart
        lead_sources: Array.isArray(sourcesRes.data) ? sourcesRes.data : [],
        recent_activity: Array.isArray(activityRes.data) ? activityRes.data : [],
      },
    };
  },

  /**
   * Get dashboard statistics only
   * Note: API interceptor unwraps { success, data } to just data
   */
  getStats: async (): Promise<{ data: DashboardStats }> => {
    const response = await api.get<BackendDashboardStats>('/stats/broker/dashboard');
    return { data: transformStats(response.data ?? {} as BackendDashboardStats) };
  },

  /**
   * Get leads chart data for the specified period
   * Note: API interceptor unwraps { success, data } to just data
   */
  getLeadsChart: async (period: TimePeriod = 'month'): Promise<{ data: LeadsChartData[] }> => {
    const response = await api.get<BackendLeadsTrend[]>('/stats/broker/leads-trend', {
      params: { period: mapPeriod(period) }
    });
    return { data: transformLeadsTrend(Array.isArray(response.data) ? response.data : []) };
  },

  /**
   * Get visits chart data for the specified period
   * Note: API interceptor unwraps { success, data } to just data
   */
  getVisitsChart: async (period: TimePeriod = 'month'): Promise<{ data: VisitsChartData[] }> => {
    const response = await api.get<BackendVisitsTrend[]>('/stats/broker/visits-trend', {
      params: { period: mapPeriod(period) }
    });
    return { data: transformVisitsTrend(Array.isArray(response.data) ? response.data : []) };
  },

  /**
   * Get commission chart data for the specified period
   * Note: Backend doesn't have commission endpoint, returns empty array
   */
  getCommissionChart: (_period: TimePeriod = 'month'): Promise<{ data: CommissionChartData[] }> =>
    Promise.resolve({ data: [] }),

  /**
   * Get lead sources distribution
   */
  getLeadSources: () => api.get<LeadSourceData[]>('/stats/broker/lead-sources'),

  /**
   * Get recent activity feed
   */
  getRecentActivity: (limit = 10) =>
    api.get<ActivityItem[]>('/stats/broker/activity', { params: { limit } }),
};

export default dashboardService;
